import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { Search, Filter, Download, Link as LinkIcon } from 'lucide-react';

const STAGE_LABELS = {
  new: 'New',
  contacted: 'Contacted',
  demo_scheduled: 'Demo Scheduled',
  trial: 'Trial',
  converted: 'Converted',
  churned: 'Churned',
};

const STAGE_COLORS = {
  new: 'bg-blue-100 text-blue-700 border-blue-200',
  contacted: 'bg-amber-100 text-amber-700 border-amber-200',
  demo_scheduled: 'bg-violet-100 text-violet-700 border-violet-200',
  trial: 'bg-orange-100 text-orange-700 border-orange-200',
  converted: 'bg-green-100 text-green-700 border-green-200',
  churned: 'bg-red-100 text-red-700 border-red-200',
};

const SOURCE_LABELS = {
  whatsapp: 'WhatsApp',
  email: 'Email',
  'in-person': 'In-person',
  call: 'Call',
  referral_link: 'Referral Link',
};

const AdminLeads = () => {
  const { getAuthHeaders, API_URL } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stageFilter, setStageFilter] = useState('all');
  const [partnerFilter, setPartnerFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchLeads();
  }, [stageFilter]);

  const fetchLeads = async () => {
    try {
      const params = stageFilter !== 'all' ? `?pipeline_stage=${stageFilter}` : '';
      const response = await axios.get(`${API_URL}/api/admin/leads${params}`, {
        headers: getAuthHeaders(),
      });
      setLeads(response.data);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const updateStage = async (leadId, stage) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/leads/${leadId}/stage?pipeline_stage=${stage}`,
        null,
        { headers: getAuthHeaders() }
      );
      setLeads(prev =>
        prev.map(l => (l.id === leadId ? { ...l, pipeline_stage: stage } : l))
      );
      toast.success('Stage updated');
    } catch (error) {
      toast.error('Failed to update stage');
    }
  };

  const uniquePartners = [...new Set(leads.map(l => l.partner_name).filter(Boolean))].sort();

  const filteredLeads = leads.filter(lead => {
    const q = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm ||
      lead.business_name?.toLowerCase().includes(q) ||
      lead.contact_name?.toLowerCase().includes(q) ||
      lead.partner_name?.toLowerCase().includes(q) ||
      lead.contact_email?.toLowerCase().includes(q);
    const matchesPartner = partnerFilter === 'all' || lead.partner_name === partnerFilter;
    return matchesSearch && matchesPartner;
  });

  const exportToCSV = () => {
    const headers = [
      'Partner', 'Business Name', 'Contact Name', 'Mobile', 'Email',
      'City', 'Industry', 'Budget', 'Source', 'Stage', 'Via Link', 'Created',
    ];
    const rows = filteredLeads.map(l => [
      l.partner_name || '',
      l.business_name || '',
      l.contact_name || '',
      l.contact_mobile || '',
      l.contact_email || '',
      l.city || '',
      l.industry || '',
      l.estimated_budget || 0,
      SOURCE_LABELS[l.source] || l.source || '',
      STAGE_LABELS[l.pipeline_stage] || l.pipeline_stage || '',
      l.referred_by_link ? 'Yes' : 'No',
      formatDate(l.created_at),
    ]);
    const csvContent = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return '—';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-leads">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#1A1A1A]">
              All Leads
            </h1>
            <p className="text-sm text-[#64748B] mt-1">
              View and manage leads across all partners
            </p>
          </div>
          <Button variant="outline" className="border-[#E2E2DF]" onClick={exportToCSV}>
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>

        {/* Filters */}
        <Card className="bg-white border-[#E2E2DF]">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <Input
                  placeholder="Search by partner, business, contact, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-10 border-[#E2E2DF]"
                />
              </div>
              <Select value={partnerFilter} onValueChange={setPartnerFilter}>
                <SelectTrigger className="w-44 h-10 border-[#E2E2DF]">
                  <SelectValue placeholder="Partner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Partners</SelectItem>
                  {uniquePartners.map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={stageFilter} onValueChange={setStageFilter}>
                <SelectTrigger className="w-44 h-10 border-[#E2E2DF]">
                  <Filter className="w-4 h-4 mr-2 text-[#64748B]" />
                  <SelectValue placeholder="Stage" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stages</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="demo_scheduled">Demo Scheduled</SelectItem>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="converted">Converted</SelectItem>
                  <SelectItem value="churned">Churned</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="bg-white border-[#E2E2DF] overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#FAFAF8] hover:bg-[#FAFAF8]">
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Partner</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Business</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Contact</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Mobile</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">City</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Industry</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Budget/mo</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Source</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Stage</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#ef785b]"></div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredLeads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-[#64748B]">
                      No leads found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLeads.map((lead, index) => (
                    <TableRow key={lead.id} className="hover:bg-[#FAFAF8]/50" data-testid={`lead-row-${index}`}>
                      <TableCell className="text-sm text-[#1A1A1A]">{lead.partner_name || '—'}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-[#1A1A1A]">{lead.business_name || '—'}</p>
                          {lead.referred_by_link && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700 mt-0.5">
                              <LinkIcon className="w-2.5 h-2.5" /> Via Link
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-[#64748B]">{lead.contact_name || '—'}</TableCell>
                      <TableCell className="text-sm text-[#64748B]">{lead.contact_mobile || '—'}</TableCell>
                      <TableCell className="text-sm text-[#64748B]">{lead.city || '—'}</TableCell>
                      <TableCell className="text-sm text-[#64748B] capitalize">{lead.industry || '—'}</TableCell>
                      <TableCell className="text-sm text-[#1A1A1A]">{formatCurrency(lead.estimated_budget)}</TableCell>
                      <TableCell className="text-sm text-[#64748B]">{SOURCE_LABELS[lead.source] || lead.source || '—'}</TableCell>
                      <TableCell>
                        <Select
                          value={lead.pipeline_stage}
                          onValueChange={(v) => updateStage(lead.id, v)}
                        >
                          <SelectTrigger className={`h-7 w-36 text-xs border font-medium ${STAGE_COLORS[lead.pipeline_stage] || ''}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(STAGE_LABELS).map(([val, label]) => (
                              <SelectItem key={val} value={val}>{label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-sm text-[#64748B]">{formatDate(lead.created_at)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminLeads;
